import React, { useContext } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { createPortal } from 'react-dom'

import { EmailFilters } from '../../components/EmailFilters/EmailFilters'
import { useZocksQuota } from '../../components/ScopedBlock/hooks'

import { IntegrationEmailThread } from '../../models/Email'

import { selectCurrentUserEmailIntegration, selectEmailCurrentUserLoading } from '../../store/emailHub/emailUsers'
import { useAppSelector } from '../../store/hooks'

import { HeaderContext } from '../LayoutWithLeftSidebar/HeaderContext'

import { EmailHubInitialization } from './Initialization/EmailHubInitialization'
import { EmailThreadView } from './ThreadView/EmailThreadView'
import { EmailHubPoller } from './Poller/EmailHubPoller'
import { EmailHeader } from './Header/EmailHeader'
import { EmailTable } from './Table/EmailTable'
import { NoEmailIntegration } from '../../components/NoEmailIntegration/NoEmailIntegration'
import { Upsell } from '../Plan/DeltaTrial/Upsell/Upsell'
import { DELTA_TRIAL_FEATURES } from '../Plan/DeltaTrial/DeltaTrialFeatures'
import { ScorecardInitialization } from '../../apps/professional-app/fa/MeetingSummary/ClientProfile/Initialization/ClientProfileInitialization'
import { useDocumentTitle } from '../../common/useDocumentTitle'

export const EmailHubV2: React.FC = () => {
  useDocumentTitle('Inbox')
  const { isFetching: quotasFetching, hasQuota } = useZocksQuota()

  if (quotasFetching) {
    return null
  }

  const emailEnabled = hasQuota('integration.emailEnabled.value')
  const clientProfileAvailable = hasQuota('ai.clientProfile.value')

  if (!emailEnabled) {
    return <Upsell feature={DELTA_TRIAL_FEATURES['inbox']} />
  }

  return (
    <div className='full-size-content'>
      {clientProfileAvailable && <ScorecardInitialization />}
      <EmailHubInitialization />
      <EmailHubPoller />
      <EmailHubContent />
    </div>
  )
}

const EmailHubContent: React.FC = () => {
  const { threadId } = useParams()
  const navigate = useNavigate()

  const { portalRef } = useContext(HeaderContext)

  const userLoading = useAppSelector(selectEmailCurrentUserLoading)
  const emailIntegration = useAppSelector(selectCurrentUserEmailIntegration)

  if (userLoading) {
    return <div className='skeleton mt-2' style={{ width: '100%', height: '300px' }} />
  }

  if (!emailIntegration) {
    return <NoEmailIntegration />
  }

  if (threadId) {
    return (
      <>
        {portalRef && portalRef.current ? createPortal(<EmailHeader threadId={threadId} />, portalRef.current) : null}
        <EmailThreadView emailConnector={emailIntegration.serviceId} threadId={threadId} />
      </>
    )
  }

  const handleRowClicked = (thread: IntegrationEmailThread) => navigate(`/email/inbox/threads/${thread.externalId}`)

  return (
    <div className='d-flex flex-column zocks-gap-2.5'>
      <EmailFilters />
      <EmailTable onRowClicked={handleRowClicked} />
    </div>
  )
}
